import React from "react";
import { Redirect, Route, type RouteProps } from "react-router-dom";

import { LoadingSpinner } from "../components/react/loading-spinner/LoadingSpinner";

interface LazyRouteProps<TPath extends string> extends Omit<RouteProps<TPath>, "component"> {
	/**
	 * @default true
	 */
	isAllowed?: boolean;
	children?: React.ReactNode;
}

/**
 * This is a wrapper over React-router Route component
 *
 * main idea is here: https://reacttraining.com/react-router/web/api/Route/render-func
 *
 * at this moment we cant use Redirect component, in that case we should provide redirect with redux actions
 *
 * NOTE: it's better to return in render function 403/404 Page Components as soon as those components will be delivered
 *
 */
export const LazyRoute = (({ isAllowed = true, render, children, ...props }) => {
	if (!isAllowed) {
		return <Redirect to="/views" />;
	}

	if (render != null) {
		return (
			<Route
				{...props}
				render={(...args) => (
					<React.Suspense fallback={<LoadingSpinner className="size-full" size="large" />}>
						{render(...args)}
					</React.Suspense>
				)}
			/>
		);
	}

	return (
		<Route {...props}>
			<React.Suspense fallback={<LoadingSpinner className="size-full" size="large" />}>{children}</React.Suspense>
		</Route>
	);
}) satisfies <TPath extends string>(props: LazyRouteProps<TPath>) => React.ReactElement;
